'use client';

import {
  Box,
  Card,
  CardContent,
  Checkbox,
  IconButton,
  Typography,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ComputerIcon from '@mui/icons-material/Computer';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import TabletIcon from '@mui/icons-material/Tablet';
import DeviceUnknownIcon from '@mui/icons-material/DeviceUnknown';

import type { ActiveSession } from '../types';

interface SessionCardProps {
  session: ActiveSession;
  isSelected: boolean;
  onToggleSelect: () => void;
  onTerminate: () => void;
  disabled?: boolean;
}

function getPlatformIcon(platform: string) {
  const lowerPlatform = platform.toLowerCase();
  if (lowerPlatform.includes('mobile') || lowerPlatform.includes('phone')) {
    return <PhoneAndroidIcon />;
  }
  if (lowerPlatform.includes('tablet')) {
    return <TabletIcon />;
  }
  if (
    lowerPlatform.includes('web') ||
    lowerPlatform.includes('desktop') ||
    lowerPlatform.includes('windows') ||
    lowerPlatform.includes('mac')
  ) {
    return <ComputerIcon />;
  }
  return <DeviceUnknownIcon />;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export function SessionCard({
  session,
  isSelected,
  onToggleSelect,
  onTerminate,
  disabled,
}: SessionCardProps) {
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1,
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s',
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Checkbox
            checked={isSelected}
            onChange={onToggleSelect}
            disabled={disabled}
            size="small"
          />

          <Box sx={{ color: 'text.secondary' }}>
            {getPlatformIcon(session.platform)}
          </Box>

          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" fontWeight={500} noWrap>
                {session.platform || 'Unknown Device'}
              </Typography>
              {session.rememberMe && (
                <Chip label="Remember Me" size="small" variant="outlined" />
              )}
            </Box>

            <Box display="flex" gap={2} mt={0.5}>
              <Typography variant="caption" color="text.secondary">
                IP: {session.lastIp}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last active: {getRelativeTime(session.lastActivity)}
              </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary" display="block">
              Created: {formatDate(session.createdAt)}
            </Typography>
          </Box>

          <IconButton
            onClick={onTerminate}
            disabled={disabled}
            color="error"
            size="small"
            title="Terminate session"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
}
